<template>
  <main class="maintenance-page">
    <section class="maintenance-card" aria-live="polite">
      <div class="maintenance-icon" aria-hidden="true">
        <i class="fa fa-gear"></i>
      </div>
      <h1>Maintenance en cours</h1>
      <p>Maintenance en cours, veuillez patienter quelques minutes</p>
      <div class="maintenance-countdown" aria-label="Temps avant la prochaine vérification">
        {{ formattedCountdown }}
      </div>
      <button type="button" class="maintenance-check-button" :disabled="isChecking" @click="checkMaintenance">
        <i class="fa" :class="isChecking ? 'fa-spinner fa-spin' : 'fa-rotate'"></i>
        <span>{{ isChecking ? 'Vérification...' : 'Vérifier maintenant' }}</span>
      </button>
      <small v-if="errorMessage" class="maintenance-error">{{ errorMessage }}</small>
    </section>
  </main>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { MAINTENANCE_CHECK_SECONDS, reloadRuntimeConfig } from '@/support/maintenanceRuntime';

export default defineComponent({
  name: 'MaintenanceView',
  emits: ['maintenance-ended'],
  data() {
    return {
      remainingSeconds: MAINTENANCE_CHECK_SECONDS,
      intervalId: null as ReturnType<typeof setInterval> | null,
      isChecking: false,
      errorMessage: '',
    };
  },
  computed: {
    formattedCountdown(): string {
      const minutes = Math.floor(this.remainingSeconds / 60);
      const seconds = this.remainingSeconds % 60;
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    },
  },
  mounted() {
    this.startCountdown();
  },
  beforeUnmount() {
    this.stopCountdown();
  },
  methods: {
    startCountdown() {
      this.stopCountdown();
      this.intervalId = setInterval(() => {
        if (this.isChecking) return;
        this.remainingSeconds = Math.max(this.remainingSeconds - 1, 0);
        if (this.remainingSeconds === 0) {
          void this.checkMaintenance();
        }
      }, 1000);
    },
    stopCountdown() {
      if (this.intervalId !== null) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    },
    resetCountdown() {
      this.remainingSeconds = MAINTENANCE_CHECK_SECONDS;
      this.startCountdown();
    },
    async checkMaintenance() {
      if (this.isChecking) return;
      this.isChecking = true;
      this.errorMessage = '';
      this.stopCountdown();

      try {
        const maintenanceEnabled = await reloadRuntimeConfig();
        if (!maintenanceEnabled) {
          this.$emit('maintenance-ended');
          return;
        }
      } catch (error) {
        this.errorMessage = 'La vérification est momentanément indisponible. Une nouvelle tentative sera effectuée automatiquement.';
      } finally {
        this.isChecking = false;
      }

      this.resetCountdown();
    },
  },
});
</script>

<style scoped>
.maintenance-page {
  align-items: center;
  background: radial-gradient(circle at top, #edf4ff 0, #f8fafc 48%, #eef2f7 100%);
  color: #0f172a;
  display: flex;
  justify-content: center;
  min-height: 100vh;
  padding: 1.5rem;
  text-align: center;
}

.maintenance-card {
  align-items: center;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid #e2e8f0;
  border-radius: 24px;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.12);
  display: flex;
  flex-direction: column;
  max-width: 560px;
  padding: clamp(2rem, 7vw, 4rem);
  width: 100%;
}

.maintenance-icon {
  align-items: center;
  background: #e8f0ff;
  border-radius: 50%;
  color: #155cff;
  display: flex;
  font-size: 3rem;
  height: 104px;
  justify-content: center;
  margin-bottom: 1.5rem;
  width: 104px;
}

.maintenance-icon i { animation: maintenance-spin 5s linear infinite; }
.maintenance-card h1 { font-size: clamp(1.75rem, 5vw, 2.5rem); font-weight: 900; margin: 0 0 0.75rem; }
.maintenance-card p { color: #64748b; font-size: 1.05rem; margin: 0; }
.maintenance-countdown { color: #155cff; font-size: clamp(2.6rem, 10vw, 4.25rem); font-variant-numeric: tabular-nums; font-weight: 900; letter-spacing: 0.08em; margin: 1.75rem 0; }
.maintenance-check-button { align-items: center; background: #155cff; border: 0; border-radius: 10px; color: #fff; display: inline-flex; font-weight: 800; gap: 0.55rem; padding: 0.75rem 1.15rem; }
.maintenance-check-button:disabled { cursor: wait; opacity: 0.7; }
.maintenance-error { color: #b91c1c; display: block; margin-top: 1rem; }

@keyframes maintenance-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .maintenance-icon i { animation: none; } }
</style>
